import React, { useState } from 'react';
import { TextField, Box, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

/**
 * FormInput - Reusable text input component
 * @param {Object} props
 * @param {string} props.name - Field name attribute
 * @param {string} props.label - Field label
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Change handler
 * @param {Function} [props.onBlur] - Blur handler
 * @param {Function} [props.onFocus] - Focus handler
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.type] - Input type (text, email, password, number, tel, url, date, time, etc.)
 * @param {boolean} [props.required] - Whether field is required
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.error] - Show error state
 * @param {string} [props.helperText] - Helper/error text
 * @param {string} [props.variant] - TextField variant (outlined, standard, filled)
 * @param {string} [props.size] - Input size (small, medium)
 * @param {number} [props.maxLength] - Maximum character length
 * @param {string} [props.className] - Additional CSS class
 * @param {Object} [props.sx] - MUI sx prop for styling
 * @param {Object} [props.rest] - Other TextField props
 * @param {boolean} [props.allowPasswordToggle] - If true and type="password", shows an eye icon to toggle visibility
 */
const FormInput = React.forwardRef((props, ref) => {
  const {
    name,
    label,
    value = '',
    onChange,
    onBlur,
    onFocus,
    placeholder = '',
    type = 'text',
    required = false,
    disabled = false,
    error = false,
    helperText = '',
    variant = 'outlined',
    size = 'small',
    maxLength,
    className,
    sx = {},
    allowPasswordToggle = false,
    InputProps,
    ...rest
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  // Determine the actual type to render
  const isPasswordField = type === 'password';
  const renderedType = isPasswordField && showPassword ? 'text' : type;

  const handleChange = (e) => {
    const newValue = e.target.value;
    
    if (maxLength && newValue.length > maxLength) {
      return;
    }
    
    onChange?.(e);
  };

  return (
    <Box className={className}>
      <TextField
        ref={ref}
        name={name}
        label={label}
        type={renderedType}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        variant={variant}
        size={size}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth
        InputProps={{
          ...InputProps, // Keep any incoming InputProps
          maxLength: maxLength, // Note: standard HTML maxLength usually goes in inputProps (lowercase i), but MUI supports it here depending on setup
          ...(isPasswordField && allowPasswordToggle && {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                  disabled={disabled}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }),
        }}
        inputProps={{
          maxLength: maxLength,
          ...(rest.inputProps || {})
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 17px) scale(1)', 
          },
          ...sx,
        }}
        {...rest}
      />
    </Box>
  );
});

FormInput.displayName = 'FormInput';

export default FormInput;